import { AlertCircle, ArrowLeftRight, CheckCircle2, Info, Keyboard, Languages, Leaf, Lock, LogOut, PanelLeftClose, PanelLeftOpen, Search, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useHotkeys } from '../lib/hotkeys';
import type { Role } from '@sense/shared';
import { canManage, useSession } from '../lib/session';
import { useCashier, usePinMode, useRole } from '../lib/cashier';
import { useToasts } from '../lib/toast';
import { CommandPalette } from './CommandPalette';
import { NAV, TABS, type NavItem } from './nav';
import { ShortcutsHelp } from './ShortcutsHelp';
import { StoreSwitcher } from './StoreSwitcher';
import { SyncPill } from './SyncPill';
import { StatusBanner } from './StoreStatus';
import { UpdatePrompt } from './UpdatePrompt';
import { useUi } from './ui-store';
import { onBack } from '../lib/backstack';
import { LANGS, useLang, useT, tr } from '../lib/i18n';
import { LanguageSheet } from './LanguageSheet';
import { useLocalOnly } from '../lib/local';

/** Pages a role may open: the built-in defaults, narrowed by the owner's choices in Roles & access. */
export function navForRole(role: Role | null | undefined, perms?: Partial<Record<Role, string[]>>, localOnly = false): NavItem[] {
  const defaults = NAV.filter((n) => !(localOnly && n.cloud) && (!n.manage || canManage(role)) && (!n.admin || role === 'owner' || role === 'manager') && (!n.owner || role === 'owner'));
  const chosen = role && role !== 'owner' ? perms?.[role] : undefined;
  return chosen ? defaults.filter((n) => n.to === '/' || chosen.includes(n.to)) : defaults;
}

function useNavItems() {
  const role = useRole();
  const perms = useSession((s) => s.me?.store.settings.rolePermissions);
  return navForRole(role, perms, useLocalOnly());
}

/** "Ctrl" or "⌘" for the mod key, matching useHotkeys. */
const MOD = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

/** Small key badge on the corner of a rail icon. */
function RailKey({ k }: { k: string }) {
  return <span aria-hidden className="num absolute -right-1 -bottom-1 min-w-4 rounded-md border border-line bg-white px-1 text-center text-[10px] leading-4 font-semibold text-ink-2">{k}</span>;
}

// Sense Fresh nav rows: 44 px, 12 px radius, the current page as a filled cocoa row.
const row = (on: boolean, compact?: boolean) =>
  `group relative flex h-11 items-center gap-3 rounded-xl text-sm transition ${compact ? 'w-11 justify-center self-center' : 'px-3'} ${on ? 'bg-primary font-semibold text-white shadow-card' : 'font-medium text-ink-2 hover:bg-surface-2'}`;

function MenuRow({ item, compact, onNavigate }: { item: NavItem; compact?: boolean; onNavigate?(): void }) {
  const Icon = item.icon;
  const t = useT();
  return (
    <NavLink to={item.to} end={item.to === '/'} onClick={onNavigate} title={compact ? tr('{0} (Alt+{1})', { 0: t(item.label), 1: item.key.toUpperCase() }) : undefined}
      className={({ isActive }) => row(isActive, compact)}>
      {({ isActive }) => (
        <>
          <span className="relative"><Icon size={20} aria-hidden />{compact && <RailKey k={item.key.toUpperCase()} />}</span>
          {!compact && <span className="flex-1">{t(item.label)}</span>}
          {!compact && <span className={`hidden text-2xs font-semibold sm:inline ${isActive ? 'text-white/70' : 'text-ink-3 opacity-0 group-hover:opacity-100'}`}>{tr('Alt {0}', { 0: item.key.toUpperCase() })}</span>}
        </>
      )}
    </NavLink>
  );
}

function Groups({ compact, onNavigate }: { compact?: boolean; onNavigate?(): void }) {
  const items = useNavItems();
  const t = useT();
  return (
    <>
      {(['Sell', 'Stock', 'Business'] as const).map((g) => (
        <div key={g} className="flex flex-col gap-1">
          {!compact && <div className="px-3 pt-4 pb-1 text-2xs font-semibold tracking-wider text-ink-3 uppercase">{t(g)}</div>}
          {compact && <div className="mx-3 my-2 border-t border-line" />}
          {items.filter((i) => i.group === g).map((i) => <MenuRow key={i.to} item={i} compact={compact} onNavigate={onNavigate} />)}
        </div>
      ))}
    </>
  );
}

function useWho() {
  const me = useSession((s) => s.me);
  const active = useCashier((s) => s.active);
  return { name: active?.name ?? me?.user.name ?? '', role: active?.role ?? me?.user.role ?? '' };
}

/** The store's mark: its first letter on the cocoa tile. */
function StoreMark({ size = 40 }: { size?: number }) {
  const me = useSession((s) => s.me);
  return (
    <span className="flex shrink-0 items-center justify-center rounded-xl bg-primary font-bold text-white" style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {me?.store.name ? me.store.name.slice(0, 1).toUpperCase() : <Leaf size={size * 0.5} />}
    </span>
  );
}

function Brand({ compact }: { compact?: boolean }) {
  const me = useSession((s) => s.me);
  const who = useWho();
  const open = useUi((s) => s.set);
  const owner = me?.user.role === 'owner' && who.role === 'owner';
  const inner = (
    <>
      <StoreMark size={compact ? 44 : 40} />
      {!compact && (
        <div className="min-w-0 flex-1 text-left leading-tight">
          <div className="truncate font-bold">{me?.store.name ?? tr('Sense Daily')}</div>
          <div className="truncate text-2xs text-ink-3">{tr('Sense Daily ·')} <span className="num">#{me?.store.store_no} · {me?.device.code}</span></div>
        </div>
      )}
      {!compact && owner && <ArrowLeftRight size={16} className="shrink-0 text-ink-3" aria-hidden />}
    </>
  );
  const cls = `flex h-20 items-center gap-3 ${compact ? 'justify-center' : 'px-2'}`;
  if (!owner) return <div className={cls}>{inner}</div>;
  return <button onClick={() => open({ stores: true })} title={tr('Switch or add store')} className={`${cls} rounded-xl hover:bg-surface-2`}>{inner}</button>;
}

function SignOut({ compact }: { compact?: boolean }) {
  const signOut = useSession((s) => s.signOut);
  const pinMode = usePinMode();
  const lock = useCashier((s) => s.lock);
  const role = useRole();
  const who = useWho();
  const t = useT();
  const lockBtn = (
    <button onClick={lock} title={tr('Lock till ({0} L)', { 0: MOD })} className={row(false, compact)}>
      <span className="relative"><Lock size={20} />{compact && <RailKey k="L" />}</span>
      {!compact && <span className="flex-1 text-left">{t('Lock / switch cashier')}</span>}
      {!compact && <span className="hidden text-2xs font-semibold text-ink-3 sm:inline">{MOD} L</span>}
    </button>
  );
  if (pinMode && role !== 'owner' && role !== 'manager') return lockBtn;
  return (
    <>
      {pinMode && lockBtn}
      {/* Who is signed in; tap to sign out (Sense Fresh profile row). */}
      <button onClick={() => { if (confirm(t('Sign out of this terminal?'))) void signOut(); }} aria-label={t('Sign out')} title={t('Sign out')}
        className={`flex items-center gap-3 rounded-xl p-2 text-left hover:bg-surface-2 ${compact ? 'justify-center' : ''}`}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-tint font-bold text-primary">{(who.name || '?').slice(0, 1).toUpperCase()}</span>
        {!compact && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">{who.name}</span>
            <span className="block truncate text-2xs text-ink-3"><span className="capitalize">{who.role.replace('_', ' ')}</span> · {t('tap to sign out')}</span>
          </span>
        )}
        {!compact && <LogOut size={16} className="text-ink-3" />}
      </button>
    </>
  );
}

function Sidebar() {
  const { pathname } = useLocation();
  // Billing needs the width: the sidebar folds to an icon rail there unless pinned open.
  const [pinned, setPinned] = useState(() => localStorage.getItem('sd.rail') === 'open');
  const compact = pathname.startsWith('/billing') && !pinned;
  const help = useUi((s) => s.set);
  return (
    <aside className={`sticky top-0 hidden h-app shrink-0 flex-col border-r border-line bg-white lg:flex ${compact ? 'w-[76px] px-2' : 'w-64 px-3'}`}>
      <Brand compact={compact} />
      <nav aria-label="Main" className="flex flex-1 flex-col overflow-y-auto pb-2"><Groups compact={compact} /></nav>
      <div className="flex flex-col gap-1 border-t border-line py-3">
        {!compact && <div className="px-1 pb-1"><SyncPill /></div>}
        <button onClick={() => help({ palette: true })} className={row(false, compact)} title={tr('Search pages and actions ({0} K)', { 0: MOD })}>
          <span className="relative"><Search size={20} />{compact && <RailKey k="K" />}</span>{!compact && <span className="flex-1 text-left">{tr('Search')}</span>}{!compact && <span className="text-2xs font-semibold text-ink-3">{MOD} K</span>}
        </button>
        <button onClick={() => help({ help: true })} className={row(false, compact)} title={tr('Keyboard shortcuts (?)')}>
          <span className="relative"><Keyboard size={20} />{compact && <RailKey k="?" />}</span>{!compact && <span className="flex-1 text-left">{tr('All shortcuts')}</span>}{!compact && <span className="text-2xs font-semibold text-ink-3">?</span>}
        </button>
        {pathname.startsWith('/billing') && (
          <button onClick={() => { const v = !pinned; setPinned(v); localStorage.setItem('sd.rail', v ? 'open' : 'rail'); }}
            className={row(false, compact)} title={compact ? tr('Expand menu') : tr('Collapse menu')}>
            {compact ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}{!compact && <span className="flex-1 text-left">{tr('Collapse menu')}</span>}
          </button>
        )}
        <SignOut compact={compact} />
      </div>
    </aside>
  );
}

function Drawer() {
  const { drawer, set } = useUi();
  const t = useT();
  const lang = useLang((s) => s.lang);
  const me = useSession((s) => s.me);
  const who = useWho();
  // The phone's Back button closes the menu first.
  useEffect(() => (drawer ? onBack(() => set({ drawer: false })) : undefined), [drawer]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!drawer) return null;
  const close = () => set({ drawer: false });
  return (
    <div className="anim-fade fixed inset-0 z-40 bg-ink/40 lg:hidden" onClick={close}>
      <div className="anim-slide flex h-full w-[min(86vw,320px)] flex-col bg-white px-3" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t('Menu')}>
        <div className="flex items-center gap-3 pt-[max(1rem,var(--sat))] pb-3">
          <span className="flex-1"><Brand /></span>
          <button aria-label={t('Close menu')} onClick={close} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2"><X size={20} /></button>
        </div>
        {me?.user.role === 'owner' && who.role === 'owner' && me.storeCount > 1 && (
          <button onClick={() => set({ drawer: false, stores: true })} className={row(false)}>
            <ArrowLeftRight size={20} /><span className="flex-1 text-left">{t('Switch store ({n})', { n: me.storeCount })}</span>
          </button>
        )}
        <nav className="flex flex-1 flex-col overflow-y-auto pb-2">
          <Groups onNavigate={close} />
          {/* Change language, as in the SME app: the current language on a badge. */}
          <button onClick={() => set({ drawer: false, language: true })} className={`${row(false)} mt-1`}>
            <Languages size={20} aria-hidden /><span className="flex-1 text-left">{t('Change language')}</span>
            <span className="rounded-lg bg-surface-2 px-2 py-1 text-2xs font-bold tracking-wider text-forest-800 uppercase">{LANGS.find((l) => l.id === lang)?.native}</span>
          </button>
        </nav>
        <div className="pb-safe flex flex-col gap-1 border-t border-line pt-2">
          <div className="px-1 py-1"><SyncPill /></div>
          <SignOut />
        </div>
      </div>
    </div>
  );
}

function BottomTabs() {
  const items = useNavItems();
  const tl = useT();
  const tabs = TABS.map((t) => items.find((i) => i.to === t)).filter(Boolean) as NavItem[];
  // Bars that sit above the tabs (e.g. Charge on billing) read the tab bar's real height, which
  // includes the phone's navigation bar.
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--tabbar-h', `${el.getBoundingClientRect().height}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <nav ref={ref} aria-label="Tabs" className="pb-safe hide-on-kb fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-white lg:hidden">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.to === '/'} className={({ isActive }) => `flex h-16 flex-1 flex-col items-center justify-center gap-1 text-2xs transition ${isActive ? 'font-bold text-primary' : 'font-medium text-ink-3'}`}>
          {({ isActive }) => (
            <>
              {/* The current tab: icon in a filled pill, like Android's own apps. */}
              <span className={`flex h-8 w-14 items-center justify-center rounded-full transition-colors ${isActive ? 'bg-primary text-white' : ''}`}>
                <t.icon size={20} aria-hidden />
              </span>
              <span className="max-w-full truncate px-0.5">{tl(t.label)}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/** Notifications: top-right on wide screens, across the top on phones and the app. Tap one to close it. */
export function Toasts() {
  const { toasts, dismiss } = useToasts();
  return (
    <div className="pointer-events-none fixed inset-x-0 top-[max(0.75rem,var(--sat))] z-[60] flex flex-col items-center gap-2 px-3 lg:inset-x-auto lg:top-5 lg:right-5 lg:items-end lg:px-0" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} role={t.tone === 'error' ? 'alert' : 'status'} onClick={() => dismiss(t.id)} title={tr('Tap to close')}
          className={`anim-drop pointer-events-auto flex w-full max-w-md cursor-pointer items-center gap-3 rounded-2xl bg-ink px-4 py-3 text-sm font-medium text-white shadow-lift lg:w-auto lg:min-w-72`}>
          {t.tone === 'error' ? <AlertCircle size={18} className="shrink-0 text-red-200" aria-hidden /> : t.tone === 'info' ? <Info size={18} className="shrink-0 text-blue-200" aria-hidden /> : <CheckCircle2 size={18} className="shrink-0 text-green-300" aria-hidden />}
          <span className="flex-1">{t.text}</span>
          {t.action && <button className="rounded-full bg-white/15 px-2.5 py-1 font-semibold" onClick={(e) => { e.stopPropagation(); t.action!.run(); dismiss(t.id); }}>{t.action.label}</button>}
          <X size={16} aria-hidden className="shrink-0 opacity-60" />
        </div>
      ))}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ui = useUi();
  const items = useNavItems();
  useEffect(() => ui.set({ drawer: false }), [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const lock = useCashier((s) => s.lock);
  const pinMode = usePinMode();
  const keys: Record<string, () => void> = {
    '!mod+k': () => ui.set({ palette: !ui.palette }),
    '!mod+l': () => { if (pinMode) lock(); },
    '?': () => ui.set({ help: true }),
    'shift+?': () => ui.set({ help: true }),
  };
  for (const i of items) keys[`!alt+${i.key}`] = () => navigate(i.to);
  useHotkeys(keys);
  // Screen changes: a short fade (opacity only, run by the compositor) instead of an abrupt swap.
  const mainRef = useRef<HTMLElement>(null);
  const firstPath = useRef(true);
  useEffect(() => {
    if (firstPath.current) { firstPath.current = false; return; }
    window.scrollTo(0, 0);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    mainRef.current?.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
  }, [pathname]);
  // Phones: swipe in from the left edge to open the side menu (the ☰ button does the same).
  useEffect(() => {
    let start: { x: number; y: number } | null = null;
    const down = (e: TouchEvent) => { const t = e.touches[0]; start = t && t.clientX < 24 && window.innerWidth < 1024 ? { x: t.clientX, y: t.clientY } : null; };
    const move = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!start || !t) return;
      if (t.clientX - start.x > 60 && Math.abs(t.clientY - start.y) < 40) { start = null; if (!document.querySelector('[role=dialog]')) ui.set({ drawer: true }); }
    };
    window.addEventListener('touchstart', down, { passive: true });
    window.addEventListener('touchmove', move, { passive: true });
    return () => { window.removeEventListener('touchstart', down); window.removeEventListener('touchmove', move); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex min-h-app">
      {/* Solid strip under the phone's status bar, so pages never scroll into it. */}
      <div aria-hidden className="pointer-events-none fixed inset-x-0 top-0 z-40 h-[var(--sat)] bg-paper" />
      <Sidebar />
      <main ref={mainRef} className={`min-w-0 flex-1 overflow-x-clip lg:pb-0 ${pathname === '/billing' ? '' : 'pb-24'}`}>{pathname !== '/billing' && <StatusBanner />}{children}</main>
      <BottomTabs />
      <Drawer />
      <CommandPalette />
      <StoreSwitcher />
      <LanguageSheet />
      <ShortcutsHelp />
      <Toasts />
      <UpdatePrompt />
    </div>
  );
}
